import { Component, OnInit } from "@angular/core";
import { UntypedFormBuilder, UntypedFormGroup, Validators } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { UserPermissions } from "./../../../../models/domains/pbm/user/user-permissions";
import { FeatureViews } from "src/app/ui/models/domains/features/custom-feature-views";
import { PageViews } from "src/app/ui/models/domains/features/custom-page-views";
import { MaskInput } from "src/app/ui/models/domains/masks/mask.model";
import { Beneficiary } from "src/app/ui/models/domains/pbm/Beneficiary/beneficiary";
import { Card } from "src/app/ui/models/domains/pbm/Beneficiary/card";
import { Dependent } from "src/app/ui/models/domains/pbm/Beneficiary/dependent";
import { OrderCardDTO } from "src/app/ui/models/domains/pbm/Beneficiary/dto/order-card-dto";
import { Employee } from "src/app/ui/models/domains/pbm/Beneficiary/employee";
import { GenderType } from "src/app/ui/models/domains/pbm/Beneficiary/gender-type";
import { JobDescription } from "src/app/ui/models/domains/pbm/Beneficiary/job-description";
import { Gender, MaritalStatus } from "src/app/ui/models/domains/pbm/Beneficiary/person";
import { BeneficiaryKey } from "src/app/ui/models/domains/pbm/Benefit/beneficiary-key";
import { Address } from "src/app/ui/models/domains/pbm/companies/address";
import { Company } from "src/app/ui/models/domains/pbm/companies/company";
import { City } from "src/app/ui/models/domains/pbm/state/city";
import { StateAcronym } from "src/app/ui/models/domains/pbm/state/state-acronym";
import { RoutePath } from "src/app/ui/models/domains/route-paths.model";
import { FormDirty } from "src/app/ui/models/utils/form-dirty.utils";
import { FeatureViewService } from "src/app/ui/services/feature-view.service";
import { LoadingService } from "src/app/ui/services/loading.service";
import { BeneficiaryService } from "src/app/ui/services/pbm/beneficiary.service";
import { CompanyService } from "src/app/ui/services/pbm/company.service";
import { JobDescriptionService } from "src/app/ui/services/pbm/job-description.service";
import { StateService } from "src/app/ui/services/pbm/state.service";
import { UserService } from "src/app/ui/services/user.service";
import { Toast } from "../../../toast/toast";
import { AddJobDescriptionDialog } from "../../shared/dialogs/add-job-description-dialog/add-job-description-dialog.component";
import { JobDescriptionDialogType } from "../../shared/dialogs/add-job-description-dialog/add-job-description-dialog.enum";
import { ConfirmDialog } from "../../shared/dialogs/confirm-dialog/confirm-dialog.component";
import { CardBlockDialogComponent } from "../dialogs/card-block-dialog/card-block-dialog.component";
import { CardOrderDialogComponent } from "../dialogs/card-order-dialog/card-order-dialog.component";
import { CardUnblockDialogComponent } from "../dialogs/card-unblock-dialog/card-unblock-dialog.component";
import { SaveBeneficiaryValidator } from "../validators/save-beneficiary-validator";
import { LimitBeneficiaryModalComponent } from "../search-beneficiary/limit-beneficiary-modal/limit-beneficiary-modal.component";
import { MatDialogRef, MatDialog } from "@angular/material/dialog";
import { MatSlideToggle } from "@angular/material/slide-toggle";

@Component({
  selector: "app-edit-beneficiary",
  templateUrl: "./edit-beneficiary.component.html",
  styleUrls: ["./edit-beneficiary.component.scss"],
})
export class EditBeneficiaryComponent implements OnInit {
  O;
  private fiscalNumber: string;
  private company: Company;
  private beneficiary: Beneficiary;
  private formDirty: FormDirty;
  private selectedTabIndex = 0;
  private confirmDialogRef: MatDialogRef<ConfirmDialog>;
  private cardOrderDialogRef: MatDialogRef<CardOrderDialogComponent>;
  private cardBlockDialogRef: MatDialogRef<CardBlockDialogComponent>;
  private cardUnblockDialogRef: MatDialogRef<CardUnblockDialogComponent>;
  private addJobDescriptionRef: MatDialogRef<AddJobDescriptionDialog>;
  private jobDescriptionList: JobDescription[] = [];
  private stateList: StateAcronym[] = [];
  private cityList: City[] = [];

  public fiscalNumberMask = MaskInput.getFicalNumberMask();
  public socialNumberMask = MaskInput.getSocialNumberMask();
  public dateMask = MaskInput.getDateMask();
  public zipCodeMask = MaskInput.getZipCodeMask();
  public telephoneMask = MaskInput.getTelephoneMask();
  public mobileMask = MaskInput.getMobileMask();
  public genderType = GenderType;

  public userNameInputBorder = "1px white solid";

  public alertLogin = false;
  public userErrorVisible = false;

  public registrationBeneficiaryForm: UntypedFormGroup = this.formBuilder.group(
    {
      beneficiaryKey: [""],
      active: [""],
      cardNumber: [""],
      name: ["", Validators.required],
      registration: [""],
      jobDescription: [""],
      admissionDate: [""],
      socialNumber: ["", Validators.required],
      birthDate: [""],
      gender: [""],
      maritalStatus: [""],
      street: [""],
      number: [""],
      complement: [""],
      district: ["", Validators.required],
      city: [{ value: 0, disabled: true }, Validators.required],
      region: [0, Validators.required],
      zipCode: [""],
      email: ["", Validators.required],
      telephone: [""],
      mobile: [""],
      needValidateEmail: [false],
      enableDelivery: [false],
      transactionPasswordRequired: [false, [Validators.required]]
    },
    {  },
  );

  constructor(
    private featureViewService: FeatureViewService,
    private activatedRoute: ActivatedRoute,
    private companyService: CompanyService,
    private beneficiaryService: BeneficiaryService,
    private loadingService: LoadingService,
    private formBuilder: UntypedFormBuilder,
    private dialog: MatDialog,
    private toast: Toast,
    private userService: UserService,
    private stateService: StateService,
    private jobDescriptionService: JobDescriptionService,
    private router: Router,
  ) {
    this.beneficiary = new Beneficiary();
    this.formDirty = new FormDirty();
  }

  async ngOnInit() {
    this.loadBeneficiaryScreen();
  }

  async loadBeneficiaryScreen() {
    this.featureViewService.activeFeatureView(FeatureViews.BeneficiaryEditBeneficiaryFeatureView, PageViews.BeneficiaryPageView);
    const ref = this.loadingService.beginLoading();
    const beneficiaryKey = new BeneficiaryKey();
    beneficiaryKey.setValue(this.activatedRoute.snapshot.params.beneficiaryKey);
    this.beneficiary.setBeneficiaryKey(beneficiaryKey);
    const userProfile = this.userService.getUserProfile();
    this.fiscalNumber = userProfile.getPreferences().getFilterText();
    try {
      this.beneficiary = await this.beneficiaryService.findBeneficiaryByKey(this.beneficiary.getBeneficiaryKey());
      this.beneficiaryService.setBeneficiary(this.beneficiary);
      this.stateList = await this.stateService.getStates();
      this.company = await this.companyService.findCompanyBy(this.fiscalNumber);
      this.jobDescriptionList = await this.jobDescriptionService.getJobDescription(this.company.getFiscalNumber());
      await this.patchRegistrationBeneficiaryForm();
      this.formDirty.markAsClean();
    } catch (error) {
      this.toast.showWarningToast(error);
      this.router.navigate([RoutePath.SearchBeneficiary]);
    } finally {
      this.loadingService.finishLoading(ref);
    }
  }

  private async patchRegistrationBeneficiaryForm(): Promise<void> {
    const state = this.stateList.find((state) => state.getValue() === this.beneficiary.getAddress().getRegion());

    if (state) {
      this.cityList = await this.stateService.getCityByStateAcronym(state);
    }

    const city = this.cityList.find((city) => city.getName() === this.beneficiary.getAddress().getCity());

    let beneficiaryJobDescription = null;

    if (this.beneficiary.getEmployee().getJobDescription()) {
      beneficiaryJobDescription = this.jobDescriptionList.find((job) => job.getKey() === this.beneficiary.getEmployee().getJobDescription().getKey());
    }
    this.registrationBeneficiaryForm.patchValue({
      beneficiaryKey: this.beneficiary.getBeneficiaryKey(),
      active: this.beneficiary.isActive(),
      cardNumber: this.beneficiary.getCard().getCardNumber(),
      name: this.beneficiary.getName(),
      registration: this.beneficiary.getEmployee().getRegistration(),
      admissionDate: this.beneficiary.getEmployee().getAdmissionDate(),
      jobDescription: beneficiaryJobDescription,
      socialNumber: this.beneficiary.getSocialNumber(),
      birthDate: this.beneficiary.getBirthDate(),
      gender: this.beneficiary.getGender().getValue(),
      maritalStatus: this.beneficiary.getMaritalStatus().getValue(),
      street: this.beneficiary.getAddress().getStreet(),
      number: this.beneficiary.getAddress().getNumber(),
      complement: this.beneficiary.getAddress().getComplement(),
      district: this.beneficiary.getAddress().getDistrict(),
      city: city ? city : 0,
      region: state ? state : 0,
      zipCode: this.beneficiary.getAddress().getZipCode(),
      email: this.beneficiary.getEmail(),
      telephone: this.beneficiary.getTelephone(),
      mobile: this.beneficiary.getMobile(),
      needValidateEmail: this.beneficiary.getNeedValidateEmail(),
      enableDelivery: this.beneficiary.getEnableDelivery(),
      transactionPasswordRequired: this.beneficiary.getTransactionPasswordRequired(),
    });

    this.registrationBeneficiaryForm.enable();
  }

  private getTabIndex(tabName: string): number {
    switch (tabName) {
      case "EDITAR BENEFICIÁRIO":
        return 0;
      case "Beneficiário":
        return 0;
      case "LISTA DE DEPENDENTES":
        return 1;
      default:
        return -1;
    }
  }

  onTabClick(clickEvent: PointerEvent) {
    const clickedTab = clickEvent.target as HTMLDivElement;
    const clickedTabIndex = this.getTabIndex(clickedTab.innerText);
    if (clickedTabIndex === -1) {
      return;
    }
    if (this.selectedTabIndex !== clickedTabIndex) {
      if (this.formDirty.isDirty()) {
        alert("Os dados foram alterados no formulário. Por favor, salve antes de prosseguir.");
      } else {
        this.selectedTabIndex = clickedTabIndex;
      }
    }
  }


  hasCompany(): boolean {
    return !!this.company;
  }
  public async getZipCode() {
    const formValue = this.registrationBeneficiaryForm.get("zipCode").value;
    var boolean = /^\d{5}[-]\d{3}$/.test(formValue);

    if (boolean) {
      const ref = this.loadingService.beginLoading();
      this.registrationBeneficiaryForm.get("zipCode").disable();
      try {
        const zip = await this.stateService.getZipCodeIntegration(formValue);
        const state = this.stateList.find((state) => state.getValue() === zip.getStateCode());
        this.registrationBeneficiaryForm.get("region").setValue(state);
        await this.onStateInputChange();

        const city = this.cityList.find((city) => city.getName() === zip.getLocality());
        this.registrationBeneficiaryForm.get("city").setValue(city);

        this.registrationBeneficiaryForm.get("street").setValue(zip.getAddress());
        this.registrationBeneficiaryForm.get("district").setValue(zip.getNeighborhood());
      } catch (error) {
        this.toast.showWarningToast("CEP não encontrado.");
      } finally {
        this.loadingService.finishLoading(ref);
        this.registrationBeneficiaryForm.get("zipCode").enable();
      }
    }
  }

  onBeneficiaryInputChange(): void {
    this.formDirty.markAsDirty();
  }

  async onStateInputChange() {
    const state = this.registrationBeneficiaryForm.get("region").value;
    if (state) {
      const ref = this.loadingService.beginLoading();
      try {
        this.cityList = await this.stateService.getCityByStateAcronym(state);
        this.registrationBeneficiaryForm.get("city").enable();
        this.registrationBeneficiaryForm.get("city").setValue(0);
      } catch (error) {
        this.cityList = [];
        this.registrationBeneficiaryForm.get("city").disable();
        this.toast.showWarningToast(error);
      } finally {
        this.loadingService.finishLoading(ref);
      }
    } else {
      this.cityList = [];
      this.registrationBeneficiaryForm.get("city").setValue(0);
      this.registrationBeneficiaryForm.get("city").disable();
    }
  }

  goToCreateDependent(): void {
    const newDependent = new Dependent();
    newDependent.setBeneficiarySocialNumber(this.beneficiary.getSocialNumber());
    this.beneficiaryService.setDependent(newDependent);
    this.router.navigate([RoutePath.RegistrationDependent]);
  }

  goToEditDependent(dependent: Dependent): void {
    this.beneficiaryService.setDependent(dependent);
    this.router.navigate([RoutePath.EditDependent]);
  }

  public openModalDependentLimit(dependent: Dependent): void {
    const dialogRef = this.dialog.open(LimitBeneficiaryModalComponent, {
      id: "status-dialog",
      disableClose: true,
      hasBackdrop: true,
      width: "860px",
      height: "450px",
      data: { dependent },
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.loadBeneficiaryScreen();
      }
    });
  }

  async saveBenficiary(): Promise<any> {
    const ref = this.loadingService.beginLoading();
    const saveBeneficiaryValidator = new SaveBeneficiaryValidator(this.registrationBeneficiaryForm);

    if (saveBeneficiaryValidator.validate().hasErrorMessages()) {
      this.toast.showWarningToast(saveBeneficiaryValidator.getValidationResult().getErrorMessage());
      this.loadingService.finishLoading(ref);
    } else {
      const newBeneficiaryConfiguration = new Beneficiary();
      newBeneficiaryConfiguration.setBeneficiaryKey(this.beneficiary.getBeneficiaryKey());

      newBeneficiaryConfiguration.setName(this.registrationBeneficiaryForm.get("name").value);
      newBeneficiaryConfiguration.setSocialNumber(this.registrationBeneficiaryForm.get("socialNumber").value.replace(/[^\d]+/g, ""));
      newBeneficiaryConfiguration.setBirthDate(this.registrationBeneficiaryForm.get("birthDate").value.replace(/[^\d]+/g, ""));
      newBeneficiaryConfiguration.setEmail(this.registrationBeneficiaryForm.get("email").value);
      newBeneficiaryConfiguration.setTelephone(this.registrationBeneficiaryForm.get("telephone").value.replace(/[^\d]+/g, ""));
      newBeneficiaryConfiguration.setMobile(this.registrationBeneficiaryForm.get("mobile").value.replace(/[^\d]+/g, ""));
      newBeneficiaryConfiguration.setNeedValidateEmail(this.registrationBeneficiaryForm.get("needValidateEmail").value);
      newBeneficiaryConfiguration.setEnableDelivery(this.registrationBeneficiaryForm.get("enableDelivery").value);
      newBeneficiaryConfiguration.setTransactionPasswordRequired(this.registrationBeneficiaryForm.get("transactionPasswordRequired").value);

      newBeneficiaryConfiguration.setDependents(this.beneficiary.getDependets());

      const employee = new Employee();
      employee.setRegistration(this.registrationBeneficiaryForm.get("registration").value);
      employee.setAdmissionDate(this.registrationBeneficiaryForm.get("admissionDate").value.replace(/[^\d]+/g, ""));
      employee.setJobDescription(this.registrationBeneficiaryForm.get("jobDescription").value);
      newBeneficiaryConfiguration.setEmployee(employee);

      const card = new Card();
      card.setCardNumber(this.registrationBeneficiaryForm.get("cardNumber").value);
      newBeneficiaryConfiguration.setCard(card);

      const personGender = new Gender();
      personGender.setGender(this.registrationBeneficiaryForm.get("gender").value);
      newBeneficiaryConfiguration.setGender(personGender);

      const personMaritalStatus = new MaritalStatus();
      personMaritalStatus.setMaritalStatus(this.registrationBeneficiaryForm.get("maritalStatus").value);
      newBeneficiaryConfiguration.setMaritalStatus(personMaritalStatus);

      const address = new Address();
      address.setStreet(this.registrationBeneficiaryForm.get("street").value);
      address.setNumber(this.registrationBeneficiaryForm.get("number").value);
      address.setComplement(this.registrationBeneficiaryForm.get("complement").value);
      address.setZipCode(this.registrationBeneficiaryForm.get("zipCode").value.replace(/[^\d]+/g, ""));
      address.setDistrict(this.registrationBeneficiaryForm.get("district").value);
      address.setCity(this.registrationBeneficiaryForm.get("city").value.getName());
      address.setRegion(this.registrationBeneficiaryForm.get("region").value.getValue());
      newBeneficiaryConfiguration.setAddress(address);

      this.beneficiary = newBeneficiaryConfiguration;
      try {
        await this.beneficiaryService.saveBeneficiary(this.fiscalNumber.replace(/[./-]/g, ""), this.beneficiary);
        this.beneficiary = await this.beneficiaryService.findBeneficiaryByKey(this.beneficiary.getBeneficiaryKey());
        this.toast.showSuccessToast("Edição de beneficiário salva com sucesso!");
      } catch (erro) {
        this.toast.showWarningToast(erro);
      } finally {
        this.formDirty.markAsClean();
        this.loadingService.finishLoading(ref);
        this.loadBeneficiaryScreen();
      }
    }
  }

  getDependents(): Dependent[] {
    return this.beneficiary.getDependets();
  }

  hasAccessToEditBeneficiary(): boolean {
    return this.userService.getLoggedUser().hasPermission(UserPermissions.EditBeneficiary);
  }

  hasAccessToBeneficiaryBlockCard(): boolean {
    return this.userService.getLoggedUser().hasPermission(UserPermissions.BeneficiaryBlockCard);
  }

  hasAccessToBeneficiaryReactivateCard(): boolean {
    return this.userService.getLoggedUser().hasPermission(UserPermissions.BeneficiaryReactivateCard);
  }

  hasAccessToEditCycleLimit(): boolean {
    return this.userService.getLoggedUser().hasPermission(UserPermissions.EditCycleLimit);
  }

  getCompany(): Company {
    return this.company;
  }

  getJobList(): JobDescription[] {
    return this.jobDescriptionList.filter((job) => job.getEnabled());
  }

  getStateList(): StateAcronym[] {
    return this.stateList;
  }

  getCityList(): City[] {
    return this.cityList;
  }

  getSelectedTabIndex(): number {
    return this.selectedTabIndex;
  }

  isBeneficiaryActive(): boolean {
    return this.beneficiary.isActive();
  }

  isBeneficiaryCardBlocked(): boolean {
    return this.beneficiary.getCard() && this.beneficiary.getCard().getIsBlocked();
  }

  isBeneficiaryCardCancelled(): boolean {
    return this.beneficiary.getCard() && this.beneficiary.getCard().getIsCancelled();
  }

  hasCard(): boolean {
    return !!this.beneficiary.getCard();
  }

  isEditableBeneficiary(): boolean {
    return this.hasAccessToEditBeneficiary() && !this.isBeneficiaryCardCancelled();
  }
  onCancelBeneficiaryCard(): void {
    this.confirmDialogRef = this.dialog.open(ConfirmDialog, {
      disableClose: true,
      hasBackdrop: true,
      height: "200px",
      width: "600px",
      data: {
        title: "Cancelamento de Cartão",
        text: "Deseja cancelar o cartão do beneficiário?",
      },
    });

    this.confirmDialogRef.componentInstance.confirmDialogEvent.subscribe(async () => {
      const loadingRef = this.loadingService.beginLoading();

      try {
        await this.beneficiaryService.cancelBeneficiaryCard(this.beneficiary.getBeneficiaryKey());
        this.beneficiary = await this.beneficiaryService.findBeneficiaryByKey(this.beneficiary.getBeneficiaryKey());
        this.toast.showSuccessToast("Cartão cancelado com sucesso");
      } catch (error) {
        this.toast.showWarningToast("Erro ao cancelar o cartão");
      } finally {
        loadingRef.closeLoading();
      }
    });
  }

  async onOrderBeneficiaryCard() {
    const loadingRef = this.loadingService.beginLoading();

    try {
      const orderCardDTO = await this.beneficiaryService.orderBeneficiaryCard(this.beneficiary.getBeneficiaryKey());
      this.beneficiary = await this.beneficiaryService.findBeneficiaryByKey(this.beneficiary.getBeneficiaryKey());
      this.patchRegistrationBeneficiaryForm();
      this.openOrderBeneficiaryCardDialog(orderCardDTO);
    } catch (error) {
      this.toast.showWarningToast(error.error.Data.ErrorMessage);
    } finally {
      loadingRef.closeLoading();
    }
  }

  private openOrderBeneficiaryCardDialog(orderCardDTO: OrderCardDTO) {
    this.cardOrderDialogRef = this.dialog.open(CardOrderDialogComponent, {
      disableClose: true,
      hasBackdrop: true,
      height: "320px",
      width: "600px",
      data: orderCardDTO,
    });
  }

  openCardBlockDialog(): void {
    this.cardBlockDialogRef = this.dialog.open(CardBlockDialogComponent, {
      disableClose: true,
      hasBackdrop: true,
      height: "345px",
      width: "600px",
      data: this.beneficiary,
    });

    this.cardBlockDialogRef.componentInstance.blockCardNumberEvent.subscribe(async (data) => {
      this.beneficiary = await this.beneficiaryService.findBeneficiaryByKey(this.beneficiary.getBeneficiaryKey());
      this.beneficiaryService.setBeneficiary(this.beneficiary);
    });
  }

  openCardUnblockDialog(): void {
    this.cardUnblockDialogRef = this.dialog.open(CardUnblockDialogComponent, {
      disableClose: true,
      hasBackdrop: true,
      height: "300px",
      width: "600px",
      data: this.beneficiary,
    });

    this.cardUnblockDialogRef.componentInstance.unblockCardNumberEvent.subscribe(async (data) => {
      this.beneficiary = await this.beneficiaryService.findBeneficiaryByKey(this.beneficiary.getBeneficiaryKey());
      this.beneficiaryService.setBeneficiary(this.beneficiary);
    });
  }

  async exportBeneficiaryUsageFile(): Promise<any> {
    const loadingRef = this.loadingService.beginLoading();
    try {
      const data = await this.beneficiaryService.exportBeneficiaryUsageFileByCardNumber(this.beneficiary.getCard().getCardNumber());
      const a = document.createElement("a");
      document.body.appendChild(a);
      const url = window.URL.createObjectURL(data);
      a.href = url;
      a.download = `Relatório_Consumo_${this.beneficiary.getSocialNumber()}.csv`;
      a.click();
      this.toast.showSuccessToast("Arquivo .csv exportado com sucesso.");
    } catch (error) {
      console.log(error);
    } finally {
      loadingRef.closeLoading();
    }
  }

  openAddJobDescriptionDialog() {
    this.addJobDescriptionRef = this.dialog.open(AddJobDescriptionDialog, {
      disableClose: true,
      hasBackdrop: true,
      height: "600px",
      width: "900px",
      data: {
        strategy: JobDescriptionDialogType.Beneficiary,
        company: this.company,
        jobDescriptionList: this.jobDescriptionList,
      },
    });

    this.addJobDescriptionRef.componentInstance.closeDialogEvent.subscribe(async (beneficiaryJobDescription: JobDescription) => {
      const loadingRef = this.loadingService.beginLoading();
      try {
        this.jobDescriptionList = await this.jobDescriptionService.getJobDescription(this.company.getFiscalNumber());
        if (beneficiaryJobDescription) {
          const peteca = this.jobDescriptionList.find((job) => job.getKey() === beneficiaryJobDescription.getKey());
          this.registrationBeneficiaryForm.get("jobDescription").setValue(peteca);
        }
      } finally {
        loadingRef.closeLoading();
      }
    });
  }
}
